import React from "react";
import styled from "styled-components";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  DatePicker,
  Input,
  Button,
  message,
} from "antd";
import dayjs from "dayjs";

const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

const RecordPaymentModal = ({
  open,
  onCancel,
  onSubmit,
  tenants = [],
  units = [],
  form,
}) => {
  // Helper function to calculate next rent due date based on rentDueDay
  const calculateNextRentDueDate = (rentDueDay) => {
    if (!rentDueDay) return null;

    const today = dayjs();
    const currentYear = today.year();
    const currentMonth = today.month();

    // Create date for this month's rent due day
    let nextDueDate = dayjs(new Date(currentYear, currentMonth, rentDueDay));

    // If the due date has already passed this month, use next month
    if (nextDueDate.isBefore(today, "day")) {
      nextDueDate = dayjs(new Date(currentYear, currentMonth + 1, rentDueDay));
    }

    // Handle edge case where rentDueDay is 31 and next month doesn't have 31 days
    // Adjust to last day of month if needed
    const lastDayOfMonth = nextDueDate.endOf("month").date();
    if (rentDueDay > lastDayOfMonth) {
      nextDueDate = nextDueDate.endOf("month");
    }

    return nextDueDate;
  };

  // Handle tenant selection - auto-select unit, fill rent, and set due date
  const handleTenantChange = (tenantId) => {
    const selectedTenant = tenants.find((t) => t.id === tenantId);
    if (selectedTenant) {
      const updates = {};

      // Auto-select the unit
      if (selectedTenant.unitId) {
        updates.unitId = selectedTenant.unitId;
      }

      // Auto-fill rent amount from tenant's monthly rent
      if (selectedTenant.monthlyRent) {
        updates.amount = selectedTenant.monthlyRent;
      }

      // Auto-set due date based on tenant's rent due day
      if (selectedTenant.rentDueDay) {
        const nextDueDate = calculateNextRentDueDate(selectedTenant.rentDueDay);
        if (nextDueDate) {
          updates.dueDate = nextDueDate;
        }
      }

      form.setFieldsValue(updates);
    }
  };

  // Handle unit selection - auto-select tenant and fill rent
  const handleUnitChange = (unitId) => {
    // Find tenant(s) assigned to this unit
    const tenantsInUnit = tenants.filter((t) => t.unitId === unitId);
    const updates = {};

    if (tenantsInUnit.length === 1) {
      // Auto-select the tenant if only one is in the unit
      updates.tenantId = tenantsInUnit[0].id;

      // Auto-fill rent amount from tenant's monthly rent
      if (tenantsInUnit[0].monthlyRent) {
        updates.amount = tenantsInUnit[0].monthlyRent;
      }

      // Auto-set due date based on tenant's rent due day
      if (tenantsInUnit[0].rentDueDay) {
        const nextDueDate = calculateNextRentDueDate(
          tenantsInUnit[0].rentDueDay
        );
        if (nextDueDate) {
          updates.dueDate = nextDueDate;
        }
      }
    } else if (tenantsInUnit.length === 0) {
      // If no tenant, try to get rent from unit
      const selectedUnit = units.find((u) => u.id === unitId);
      if (selectedUnit && selectedUnit.monthlyRent) {
        updates.amount = selectedUnit.monthlyRent;
      }
    } else {
      // Multiple tenants - try to get rent from unit
      const selectedUnit = units.find((u) => u.id === unitId);
      if (selectedUnit && selectedUnit.monthlyRent) {
        updates.amount = selectedUnit.monthlyRent;
      }
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
    }
  };

  return (
    <StyledModal
      title="Record Payment"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
      centered
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <Item
              label="Tenant"
              name="tenantId"
              rules={[{ required: true, message: "Please select a tenant" }]}
            >
              <Select placeholder="Select tenant" onChange={handleTenantChange}>
                {tenants.map((tenant) => (
                  <Option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label="Unit"
              name="unitId"
              rules={[{ required: true, message: "Please select a unit" }]}
            >
              <Select placeholder="Select unit" onChange={handleUnitChange}>
                {units.map((unit) => (
                  <Option key={unit.id} value={unit.id}>
                    {unit.name}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Item
              label="Amount"
              name="amount"
              rules={[{ required: true, message: "Enter amount" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter amount"
                min={0}
                step={0.01}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label="Due Date"
              name="dueDate"
              rules={[{ required: true, message: "Please select due date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Select due date"
              />
            </Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Item label="Payment Type" name="paymentType" initialValue="rent">
              <Select placeholder="Select payment type">
                <Option value="rent">Rent (Recurring)</Option>
                <Option value="one_off_rent">Rent (One-off)</Option>
                <Option value="utility">Utility</Option>
                <Option value="late_fee">Late Fee</Option>
                <Option value="security_deposit">Security Deposit</Option>
                <Option value="other">Other</Option>
              </Select>
            </Item>
          </Col>
          <Col span={12}>
            <Item label="Payment Method" name="paymentMethod">
              <Select placeholder="Select method (optional)" allowClear>
                <Option value="ach">ACH/Bank Transfer</Option>
                <Option value="credit_card">Credit Card</Option>
                <Option value="check">Check</Option>
                <Option value="cash">Cash</Option>
                <Option value="stripe">Stripe</Option>
                <Option value="paypal">PayPal</Option>
                <Option value="manual">Manual</Option>
              </Select>
            </Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Item label="Status" name="status" initialValue="pending">
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="paid">Paid</Option>
                <Option value="overdue">Overdue</Option>
              </Select>
            </Item>
          </Col>
        </Row>

        <Item label="Notes" name="notes">
          <TextArea
            rows={3}
            placeholder="Additional notes about this payment"
          />
        </Item>

        <Row gutter={16}>
          <Col span={12}>
            <Button type="default" onClick={onCancel} style={{ width: "100%" }}>
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
              Record Payment
            </Button>
          </Col>
        </Row>
      </Form>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  --record-payment-header-height: 56px;
  --record-payment-close-size: 32px;
  --lo-modal-header-height: var(--record-payment-header-height);
  --lo-modal-close-size: var(--record-payment-close-size);

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 52px 16px 20px;
    min-height: var(--record-payment-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1.2;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: calc(
      (var(--record-payment-header-height) - var(--record-payment-close-size)) /
        2
    ) !important;
    right: 16px;
    width: var(--record-payment-close-size) !important;
    height: var(--record-payment-close-size) !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: rgba(255, 255, 255, 0.8) !important;
      fill: rgba(255, 255, 255, 0.8) !important;
    }

    .ant-modal-close-x,
    .ant-modal-close-icon,
    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      line-height: 1 !important;
    }
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    max-width: 90vw;
    margin: 20px auto;
  }

  /* Vertically align Select dropdown carats */
  .ant-select {
    position: relative !important;

    .ant-select-selector {
      position: relative !important;
      display: flex !important;
      align-items: center !important;
      padding-right: 30px !important;
      min-height: 32px !important;
      height: auto !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      display: flex !important;
      align-items: center !important;
      height: 100% !important;
      padding: 0 !important;
    }

    .ant-select-arrow {
      position: absolute !important;
      right: 11px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      color: var(--color-text) !important;
      font-size: 12px !important;
      line-height: 1 !important;
      height: auto !important;
      width: auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      pointer-events: none !important;

      .anticon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        line-height: 1 !important;
        margin: 0 !important;
      }

      svg {
        display: inline-block !important;
        vertical-align: middle !important;
      }
    }
  }

  .ant-select-single .ant-select-selector {
    height: auto !important;
    min-height: 32px !important;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    --record-payment-header-height: 48px;
    --record-payment-close-size: 28px;

    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 48px 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      top: calc(
        (var(--record-payment-header-height) - var(--record-payment-close-size)) /
          2
      ) !important;
      right: 16px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    --record-payment-header-height: 44px;
    --record-payment-close-size: 26px;

    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 44px 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }

    .ant-modal-close {
      top: calc(
        (var(--record-payment-header-height) - var(--record-payment-close-size)) /
          2
      ) !important;
      right: 12px;
      font-size: 12px;
    }
  }
`;

export default RecordPaymentModal;
